import { Flex } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useAgentDashboard } from '../../../../api/agent-miner-client/agent-miner-client.hooks';
import { SingleValueCard } from '../../../../components/overview-card/single-value-card';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { useProcessAnalysisStore } from '../../../../store/process-analysis-store/process-analysis-store';
import { useTimeAllocation } from '../../../../store/process-analysis-store/process-analysis-store.hooks';
import { formatKPIValue } from '../../overview/overview.helpers';
import { getLastNonNullValue, sumMeasureValues } from '../main-section/main-section.helpers';

/**
 * Agents-tab variant of the Process Analysis KPI overview row. Renders four
 * agent-specific summary tiles (median cycle times, total throughput, agent
 * throughput ratio). Tiles render their value as a Skeleton on first paint
 * (no cached agent dashboard data yet) so the row keeps its footprint while
 * the dashboard query resolves.
 *
 * @returns {JSX.Element} The agents KPI overview row
 */
export const AgentsKPIOverview = () => {
  const { t } = useTranslation();
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const portfolioId = useGlobalStore((state) => state.portfolio?.id);
  const startDate = useProcessAnalysisStore((state) => state.startDate);
  const endDate = useProcessAnalysisStore((state) => state.endDate);
  const selectedTaskType = useProcessAnalysisStore((state) => state.selectedTaskType);
  const selectedPoints = useProcessAnalysisStore((state) => state.selectedPoints);
  const timeAllocationType = useTimeAllocation();

  const { data, query } = useAgentDashboard({
    organization_id: organizationId ?? '',
    portfolio_id: portfolioId ?? '',
    start_date: startDate,
    end_date: endDate,
    time_allocation_type: timeAllocationType,
    ...(selectedTaskType ? { task_type: selectedTaskType } : {}),
    ...(selectedPoints != null ? { points: selectedPoints } : {}),
  });

  const showSkeleton = query.isLoading && !data;
  const agentMedian = data?.cycle_time_analysis?.agent?.median_cycle_time_days;
  const nonAgentMedian = data?.cycle_time_analysis?.non_agent?.median_cycle_time_days;

  return (
    <Flex direction="row" gap={24}>
      <SingleValueCard
        title="Agent Median Cycle Time"
        icon="timer"
        value={agentMedian !== null && agentMedian !== undefined ? formatKPIValue(agentMedian) : '-'}
        unit="days"
        isLoading={showSkeleton}
      />
      <SingleValueCard
        title="Non-Agent Median Cycle Time"
        icon="timer"
        value={nonAgentMedian !== null && nonAgentMedian !== undefined ? formatKPIValue(nonAgentMedian) : '-'}
        unit="days"
        isLoading={showSkeleton}
      />
      <SingleValueCard
        title={t('process_analysis.kpi.total_throughput')}
        icon="trending_up"
        value={formatKPIValue(sumMeasureValues(data?.throughput))}
        unit={t('common.work_units').toLowerCase()}
        isLoading={showSkeleton}
      />
      <SingleValueCard
        title="Agent Throughput Ratio"
        icon="percent"
        value={
          data?.agent_throughput_ratio &&
          Object.values(data.agent_throughput_ratio).some((value) => value !== null && value !== undefined)
            ? `${formatKPIValue(getLastNonNullValue(data.agent_throughput_ratio))}%`
            : '-'
        }
        isLoading={showSkeleton}
      />
    </Flex>
  );
};
